"use client";

import { useEffect, useState } from "react";
import type { EmailDetail, NormalizedEmail } from "@/lib/emailCleaner/types";
import { aiGateway, AiGatewayError } from "@/lib/emailCleaner/aiGateway";
import { fetchMessageDetail } from "@/lib/emailCleaner/gmail";

type Props = {
  emails: NormalizedEmail[];
  accessToken: string;
  selectedId: string | null;
  selectedEmail: NormalizedEmail | null;
  onSelect: (id: string) => void;
};

type ToolKey = "summarize" | "reply" | "rewrite" | "scam" | "calendar" | "tasks";

export default function EmailDetailPanel({ emails, accessToken, selectedId, onSelect }: Props) {
  const [detail, setDetail] = useState<EmailDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [tone, setTone] = useState("Professional");
  const [rewriteStyle, setRewriteStyle] = useState("professional");
  const [draft, setDraft] = useState("");
  const [running, setRunning] = useState<ToolKey | null>(null);
  const [results, setResults] = useState<Partial<Record<ToolKey, string>>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId) {
      // Clearing detail state when the selection is cleared is the sync-with-props
      // behavior this effect exists for.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    setResults({});
    setError(null);
    fetchMessageDetail(accessToken, selectedId)
      .then((data) => setDetail(data))
      .finally(() => setLoadingDetail(false));
  }, [selectedId, accessToken]);

  async function run(tool: ToolKey) {
    if (!detail) return;
    setRunning(tool);
    setError(null);
    try {
      if (tool === "summarize") {
        const { summary } = await aiGateway.summarize("email", detail.body);
        setResults((r) => ({ ...r, summarize: summary }));
      } else if (tool === "reply") {
        const { reply } = await aiGateway.reply(detail.body, tone);
        setResults((r) => ({ ...r, reply }));
        setDraft(reply);
      } else if (tool === "rewrite") {
        const { rewritten } = await aiGateway.rewrite(draft || detail.body, rewriteStyle);
        setResults((r) => ({ ...r, rewrite: rewritten }));
      } else if (tool === "scam") {
        const { is_scam, reason } = await aiGateway.scamCheck(detail.body);
        setResults((r) => ({ ...r, scam: `${is_scam ? "⚠️ Likely scam" : "✅ Looks safe"} — ${reason}` }));
      } else if (tool === "calendar") {
        const { events } = await aiGateway.extractCalendarEvents(detail.body);
        setResults((r) => ({
          ...r,
          calendar: events.length ? events.map((e) => `${e.title} — ${e.date_time}${e.location ? ` @ ${e.location}` : ""}`).join("\n") : "No events found.",
        }));
      } else if (tool === "tasks") {
        const { tasks } = await aiGateway.extractTasks(detail.body);
        setResults((r) => ({ ...r, tasks: tasks.length ? tasks.join("\n") : "No action items found." }));
      }
    } catch (err) {
      setError(err instanceof AiGatewayError ? err.message : "That action couldn't complete right now.");
    } finally {
      setRunning(null);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <div className="max-h-[32rem] overflow-y-auto rounded-2xl border border-slate-200/80 bg-white/80 dark:border-slate-800 dark:bg-[#0f172a]/80">
        {emails.length === 0 && <p className="p-4 text-sm text-slate-500 dark:text-slate-400">No messages loaded yet.</p>}
        {emails.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => onSelect(e.id)}
            className={`block w-full truncate border-b border-slate-100 px-4 py-3 text-left text-sm last:border-none dark:border-slate-800 ${
              selectedId === e.id ? "bg-violet-50 dark:bg-violet-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
            }`}
          >
            <p className="truncate font-medium text-slate-800 dark:text-slate-200">{e.from}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-500">{e.subject}</p>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-6 dark:border-slate-800 dark:bg-[#0f172a]/80">
        {!selectedId && <p className="text-sm text-slate-500 dark:text-slate-400">Pick a message on the left to run AI tools on it.</p>}
        {loadingDetail && <p className="text-sm text-slate-500 dark:text-slate-400">Loading message…</p>}

        {detail && !loadingDetail && (
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{detail.subject}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {detail.from} · {detail.date}
            </p>
            <p className="mt-3 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
              {detail.body.slice(0, 800) || "(no readable body)"}
            </p>

            {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

            <div className="mt-4 flex flex-wrap gap-2">
              <ToolButton label="Summarize" active={running === "summarize"} onClick={() => run("summarize")} />
              <ToolButton label="Scam check" active={running === "scam"} onClick={() => run("scam")} />
              <ToolButton label="Extract events" active={running === "calendar"} onClick={() => run("calendar")} />
              <ToolButton label="Extract tasks" active={running === "tasks"} onClick={() => run("tasks")} />
            </div>

            {(["summarize", "scam", "calendar", "tasks"] as ToolKey[]).map(
              (key) =>
                results[key] && (
                  <p key={key} className="mt-3 whitespace-pre-wrap rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-sm text-slate-700 dark:border-violet-800/60 dark:bg-violet-500/10 dark:text-slate-200">
                    {results[key]}
                  </p>
                ),
            )}

            <div className="mt-6 border-t border-slate-100 pt-4 dark:border-slate-800">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Draft a reply</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
                >
                  {["Professional", "Friendly", "Assertive", "Apologetic"].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <ToolButton label="Draft reply" active={running === "reply"} onClick={() => run("reply")} />
              </div>

              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Reply draft appears here — edit freely, then rewrite/translate below."
                className="mt-3 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
                rows={4}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={rewriteStyle}
                  onChange={(e) => setRewriteStyle(e.target.value)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
                >
                  <option value="professional">More professional</option>
                  <option value="casual">More casual</option>
                  <option value="concise">More concise</option>
                  <option value="translate:Spanish">Translate: Spanish</option>
                  <option value="translate:French">Translate: French</option>
                  <option value="translate:Hindi">Translate: Hindi</option>
                </select>
                <ToolButton label="Rewrite / translate" active={running === "rewrite"} onClick={() => run("rewrite")} />
              </div>
              {results.rewrite && (
                <p className="mt-3 whitespace-pre-wrap rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-sm text-slate-700 dark:border-violet-800/60 dark:bg-violet-500/10 dark:text-slate-200">
                  {results.rewrite}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={active}
      className="rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-violet-300 hover:text-violet-700 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200"
    >
      {active ? "Running…" : label}
    </button>
  );
}
